import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { intersection } from "lodash";

import { useAppSelector } from "app/hooks";
import type {
  BaseMenuItem,
  MenuItem
} from "components/section-side-menu/section-side-menu.component";
import { selectCurrentUser } from "features/auth/auth-slice";
import usePatientSectionTabs from "features/patients/patient/use-patient-section-tabs";

import type { QuickLauncherAction } from "./quick-launcher.types";
import type { QuickLauncherContext } from "./use-quick-launcher-context.hook";

/**
 * The chart's tabs carry a rendered `<Icon />` element, while the launcher
 * renders a component. Wrapping is cheaper than widening the launcher's type
 * for one caller; the `fontSize` prop the launcher passes is simply ignored.
 */
const toIconComponent = (element: JSX.Element): React.ElementType => {
  const TabIcon = () => element;
  TabIcon.displayName = "QuickLauncherTabIcon";
  return TabIcon;
};

/**
 * "Go to <tab>" for every tab of the current chart, sub-tabs included
 * (Hearing Aids › Orders, Touchpoints › Messages, …).
 *
 * `usePatientSectionTabs` is the same hook the chart's side menu renders
 * from, and it already applies the flag/role/practice gating for top-level
 * tabs — it is safe to call outside the chart (Settings and Administration
 * already do). Sub-items are gated by `SectionSideMenuListItem` at render
 * time rather than by the hook, so that filter is mirrored here.
 */
export default function useQuickLauncherPatientTabActions(
  context: QuickLauncherContext | null
): QuickLauncherAction[] {
  const navigate = useNavigate();
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const tabs = usePatientSectionTabs();

  return useMemo(() => {
    if (!context) return [];
    const chart = `/patients/${context.patientId}`;
    const userRoles = currentUser?.groups?.map(({ name }) => name) ?? [];
    // Same two checks `SectionSideMenuListItem` applies to sub-items.
    const canSee = ({ roles, featureFlag }: BaseMenuItem) =>
      (!featureFlag || Boolean(flags[featureFlag])) &&
      (!roles ||
        (Boolean(currentUser) && intersection(roles, userRoles).length > 0));

    const toAction = (
      item: BaseMenuItem,
      path: string,
      label: string
    ): QuickLauncherAction => ({
      id: `patient-tab:${path}`,
      label,
      description: item.tooltip || `Open the ${item.primary} tab`,
      group: "This patient",
      Icon: toIconComponent(item.Icon),
      keywords: ["go to", "open", "tab", item.to],
      perform: () => navigate(`${chart}/${path}`)
    });

    return tabs.flatMap((tab: MenuItem) => {
      const own = tab.containerOnly
        ? []
        : [toAction(tab, tab.to, `Go to ${tab.primary}`)];
      const subs = (tab.subItems ?? [])
        .filter(canSee)
        .map((sub) =>
          toAction(
            sub,
            `${tab.to}/${sub.to}`,
            `Go to ${tab.primary} › ${sub.primary}`
          )
        );
      return [...own, ...subs];
    });
  }, [context, tabs, flags, currentUser, navigate]);
}
